import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Gift, Flame, MapPin, ArrowRight, PartyPopper, Rocket, Lightbulb } from "lucide-react";

const FLAG = "proxi_welcome_offer";

/** Popup de bienvenue affichée une seule fois, juste après l'inscription.
 *  Met en avant le « bon plan du jour » (s'il existe) + infos de lancement et invitation à suggérer. */
export default function WelcomeOfferDialog() {
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(FLAG) !== "1") return;
    sessionStorage.removeItem(FLAG); // consommé → ne s'affiche qu'une fois
    setOpen(true);
    api.get("/public/deal-of-the-day")
      .then((r) => { const o = r.data?.offer || null; if (o) setOffer(o); })
      .catch(() => {});
  }, []);

  if (!open) return null;

  const cover = offer && ((Array.isArray(offer.images) && offer.images[0]) || offer.image);
  const city = offer && (offer.business?.city || offer.city);

  const profiter = () => { setOpen(false); if (offer) navigate(`/offre/${offer.id}`); else navigate("/offres"); };
  const suggerer = () => { setOpen(false); navigate("/suggestions"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border-0 max-h-[92vh] overflow-y-auto" data-testid="welcome-offer-dialog">
        {/* Bandeau festif */}
        <div className="relative px-6 pt-7 pb-6 text-center text-white bg-gradient-to-br from-[#F7941E] to-[#E8521B]">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-white/20 grid place-items-center backdrop-blur-sm">
            <PartyPopper size={28} className="text-white" />
          </div>
          <h2 className="mt-3 font-heading text-2xl font-extrabold leading-tight" data-testid="welcome-offer-title">
            Bienvenue sur PROXIreduc-28&nbsp;🎉
          </h2>
          <p className="mt-1 text-sm text-white/90 font-medium">
            Votre compte est créé — bienvenue dans le réseau local d'Eure-et-Loir&nbsp;!
          </p>
        </div>

        <div className="px-6 pb-6 -mt-3">
          {/* Carte offre (cadeau de bienvenue) — si disponible */}
          {offer && (
            <div className="rounded-2xl overflow-hidden border border-orange-200 bg-white shadow-lg shadow-orange-100/60">
              <div className="relative h-36">
                {cover
                  ? <img src={cover} alt={offer.title} className="w-full h-full object-cover" data-testid="welcome-offer-image" />
                  : <div className="w-full h-full bg-gradient-to-br from-orange-100 to-emerald-50" />}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E05638] text-white text-[11px] font-extrabold uppercase tracking-wide shadow">
                  <Gift size={13} /> Cadeau de bienvenue
                </div>
                {offer.discount && (
                  <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg bg-white/95 text-[#E05638] text-sm font-extrabold shadow">
                    {offer.discount}
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#E05638]">
                  <Flame size={12} /> Bon plan du jour
                </div>
                <div className="mt-1 font-heading font-extrabold text-slate-900 leading-tight line-clamp-2" data-testid="welcome-offer-name">
                  {offer.title}
                </div>
                <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500 min-w-0">
                  <MapPin size={12} className="shrink-0" />
                  <span className="truncate">{offer.business?.name}{city ? ` · ${city}` : ""}</span>
                </div>
              </div>
            </div>
          )}

          {/* Infos de lancement + invitation à suggérer */}
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="welcome-launch-info">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-emerald-700">
              <Rocket size={13} /> En phase de lancement
            </div>
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
              PROXIreduc-28 a été mis en ligne le <strong>vendredi 16 octobre 2026</strong> et est en pleine
              <strong> phase de lancement</strong>. Vous ne trouvez pas encore le professionnel ou l'activité que
              vous cherchez&nbsp;? Un peu de patience&nbsp;: le réseau s'agrandit chaque jour&nbsp;!
            </p>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              En attendant, suggérez-nous un commerce ou un professionnel que vous connaissez&nbsp;: il recevra une
              invitation à rejoindre la plateforme. Toute autre suggestion est la bienvenue&nbsp;!
            </p>
            <button onClick={suggerer}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline"
                    data-testid="welcome-suggest-btn">
              <Lightbulb size={15} /> Faire une suggestion <ArrowRight size={14} />
            </button>
          </div>

          <button onClick={profiter}
                  className="mt-4 w-full h-12 rounded-xl btn-shine text-white font-semibold inline-flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
                  data-testid="welcome-offer-cta">
            {offer ? "J'en profite" : "Explorer les bons plans"} <ArrowRight size={16} />
          </button>
          <button onClick={() => setOpen(false)}
                  className="mt-2 w-full text-center text-sm text-slate-500 hover:text-slate-700"
                  data-testid="welcome-offer-later">
            Plus tard
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
